import { useCallback } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import drawingService from "services/drawing.service"
import { isAxiosError } from "helpers/utils.helper"
import { useFillesTableStore } from "store/table.store"
import { useMessage } from "hooks/message.hook"
import {
	getOwnerChangeErrorMessage,
	OwnerChangeFailedError,
	OwnerChangePollTimeoutError,
} from "helpers/drawingOwnerChange.helper"
import {
	CHANGE_OWNER_MESSAGES,
	CHANGE_OWNER_POLL_INTERVAL_MS,
	CHANGE_OWNER_POLL_TIMEOUT_MS,
} from "helpers/drawingOwnerChange.constants"
import type { TDrawingStatus } from "types/drawer.type"
import type { TChangeDrawingOwnerArgs, TDrawingOwnerChangeJobResponse } from "types/drawingOwnerChange.type"

const MAX_RETRIES = 3

/**
 * Shared options for the drawing queries the file-info drawer fires, all of which 403 for a user
 * without access to the drawing. Scoped to these hooks on purpose — not a global query default.
 *
 * - `retry`: a 4xx never succeeds on retry, and the default 3 retries with backoff keep the query
 *   `isPending` with `error: null` for ~7s, so consumers can't tell "loading" from "denied".
 * - `retryOnMount`: an errored query must not refetch every time a new observer subscribes. The
 *   drawer header gates `<ProjectNumber />` on `isDrawingPending`, and ProjectNumber itself calls
 *   `useDrawing`, so settling the query mounts an observer that would refetch it — error, commit,
 *   mount, refetch, forever.
 */
const deniableQueryOptions = {
	retry: (failureCount: number, error: Error) => {
		const status = isAxiosError(error) ? error.response?.status : undefined
		if (status && status >= 400 && status < 500) return false
		return failureCount < MAX_RETRIES
	},
	retryOnMount: false,
}

export const useDrawings = () => {
	const { tableParams } = useFillesTableStore()

	return useQuery({
		queryKey: ["drawings", tableParams],
		queryFn: () => drawingService.getDrawings(tableParams),
		select: ({ data }) => data,
	})
}

export const useDrawing = (id: number | null) => {
	return useQuery({
		queryKey: ["drawingById", id],
		queryFn: () => drawingService.getDrawing(id),
		select: ({ data }) => data,
		enabled: !!id,
		...deniableQueryOptions,
	})
}

export const useDrawingWithProcessings = (drawingId: number | null, processingId: string | number | null) => {
	return useQuery({
		queryKey: ["drawingWithProcessing", drawingId, processingId],
		queryFn: () => drawingService.getDrawingWithProcessings(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId,
		...deniableQueryOptions,
	})
}

export const useDocuments = (drawingId: number, processingId: string | number | null, drawingStatus: string) => {
	const isEnabled = !!drawingId && (drawingStatus === "uploaded" || !!processingId)
	return useQuery({
		queryKey: ["documents", drawingId, processingId],
		queryFn: () => drawingService.getDocuments(drawingId, processingId, drawingStatus),
		select: ({ data }) => data,
		enabled: isEnabled,
		...deniableQueryOptions,
	})
}

export const useDrawingKeywords = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["drawingKeywords", drawingId, processingId],
		queryFn: () => drawingService.getDrawingKeywords(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && drawingStatus === "processed",
	})
}

export const useSpecificationKeywords = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["specificationKeywords", drawingId, processingId],
		queryFn: () => drawingService.getSpecificationKeywords(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && drawingStatus === "processed",
	})
}

export const useDrawingKeywordsConfig = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["drawingKeywordsConfig", drawingId, processingId],
		queryFn: () => drawingService.getDrawingKeywordsConfig(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && drawingStatus === "processed",
	})
}

export const useDrawingSpecificationKeywordsConfig = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["drawingSpecificationKeywordsConfig", drawingId, processingId],
		queryFn: () => drawingService.getDrawingSpecificationKeywordsConfig(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && drawingStatus === "processed",
	})
}

export const useDrawingObjects = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["drawingObjects", drawingId, processingId],
		queryFn: () => drawingService.getDrawingObjects(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && drawingStatus === "processed",
	})
}

export const useDrawingObjectsConfig = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["drawingObjectsConfig", drawingId, processingId],
		queryFn: () => drawingService.getDrawingObjectsConfig(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && drawingStatus === "processed",
	})
}

export const useDeleteDrawing = () => {
	return useMutation({
		mutationKey: ["deleteDrawing"],
		mutationFn: (id: number | undefined) => drawingService.delDrawing(id),
	})
}

export const useUpdateDrawing = () => {
	return useMutation({
		mutationKey: ["updateDrawing"],
		mutationFn: ({ id, projectNumber }: { id: number; projectNumber: string | null }) =>
			drawingService.updateDrawing(id, { project_number: projectNumber }),
	})
}

export const useProcessings = (id: number) => {
	return useQuery({
		queryKey: ["processings", id],
		queryFn: () => drawingService.getProcessingRuns(id),
		select: ({ data }) => data,
		enabled: !!id,
		...deniableQueryOptions,
	})
}

export const useDuplicateDrawing = () => {
	return useMutation({
		mutationKey: ["duplicateDrawing"],
		mutationFn: (id: number) => drawingService.duplicateDrawing(id),
	})
}

export const useDrawingVersions = (
	drawingId: number | null,
	processingId: string | number | null,
	drawingStatus: TDrawingStatus,
) => {
	return useQuery({
		queryKey: ["drawingVersions", drawingId, processingId],
		queryFn: () => drawingService.getDrawingVersions(drawingId, processingId),
		select: ({ data }) => data,
		enabled: !!drawingId && !!processingId && (drawingStatus === "processed" || drawingStatus === "failed"),
	})
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// The job keeps running server-side whatever happens to one poll, so a transient failure (network,
// 5xx) is retried rather than reported as a failed change; a 4xx is final.
const MAX_CONSECUTIVE_POLL_FAILURES = 3

const pollOwnerChange = async (drawingId: number, jobId: number): Promise<TDrawingOwnerChangeJobResponse> => {
	const deadline = Date.now() + CHANGE_OWNER_POLL_TIMEOUT_MS
	let failures = 0
	for (;;) {
		try {
			const { data } = await drawingService.getOwnerChangeStatus(drawingId, jobId)
			failures = 0
			if (data.status === "done" || data.status === "failed") return data
		} catch (error) {
			const status = isAxiosError(error) ? error.response?.status : undefined
			const isFinal = status !== undefined && status >= 400 && status < 500
			if (isFinal || ++failures >= MAX_CONSECUTIVE_POLL_FAILURES) throw error
		}
		if (Date.now() >= deadline) throw new OwnerChangePollTimeoutError()
		await sleep(CHANGE_OWNER_POLL_INTERVAL_MS)
	}
}

// Drawing owner change (REQ-473): start the background job, poll it to a terminal state, then refetch
// everything that shows the owner. Same toast lifecycle as useDownloadQuotationResultPdf; the busy
// flag is per drawing (changingOwnerIds), so the row shows a spinner and cannot be started twice.
export const useChangeDrawingOwner = () => {
	const queryClient = useQueryClient()
	const { loading, resolve } = useMessage()

	const changeDrawingOwner = useCallback(
		async ({ drawingId, newOwnerId, newOwnerName }: TChangeDrawingOwnerArgs) => {
			const { changingOwnerIds, addChangingOwnerId, removeChangingOwnerId } = useFillesTableStore.getState()
			if (changingOwnerIds.includes(drawingId)) return
			addChangingOwnerId(drawingId)
			const toastId = loading(CHANGE_OWNER_MESSAGES.pending)
			let jobStarted = false

			try {
				const { data: started } = await drawingService.startOwnerChange(drawingId, newOwnerId)
				jobStarted = true
				const result = await pollOwnerChange(drawingId, started.job_id)
				if (result.status === "failed") throw new OwnerChangeFailedError(result.error_message)
				resolve(toastId, CHANGE_OWNER_MESSAGES.success(newOwnerName), "success")
			} catch (error) {
				resolve(toastId, getOwnerChangeErrorMessage(error), "error")
			} finally {
				removeChangingOwnerId(drawingId)
				// Refetch whenever a job ran, not only on success: if polling gave up, the change may
				// still have landed, and a stale owner would invite a retry the backend then rejects.
				if (jobStarted) {
					void queryClient.invalidateQueries({ queryKey: ["drawings"] })
					void queryClient.invalidateQueries({ queryKey: ["drawingById", drawingId] })
					void queryClient.invalidateQueries({ queryKey: ["drawingWithProcessing", drawingId] })
				}
			}
		},
		[loading, resolve, queryClient],
	)

	return { changeDrawingOwner }
}
